import React from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { Sun, Moon } from 'react-feather';
import useSettings from 'src/hooks/useSettings';
import {
  THEME_MODES,
  nextThemeMode,
} from 'src/utils/resolveTheme';
import { INVENTORY_UI_FLAGS } from 'src/views/listView/inventoryUiFlags';

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.type === 'light' ? '#0F172A' : '#ffffff',
    padding: 8,
  },
  /** Circle chrome matching inventory header logout buttons. */
  circle: {
    width: 50,
    height: 50,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: theme.palette.type === 'light' ? '#ffffff' : '#191f29',
    border: theme.palette.type === 'light'
      ? 'solid 1px rgba(15, 23, 42, 0.12)'
      : 'solid 1px #0c111a',
    color: theme.palette.type === 'light' ? '#0F172A' : '#ffffff',
    boxShadow: theme.palette.type === 'light'
      ? '0 1px 3px rgba(15, 23, 42, 0.08)'
      : 'none',
    '&:hover': {
      backgroundColor: theme.palette.type === 'light' ? '#F8FAFC' : '#232a36',
    },
  },
  /** Compact control for the fixed dark TopBar. */
  topBar: {
    color: '#ffffff',
    marginRight: 8,
    flexShrink: 0,
  },
}));

const MODE_LABEL = {
  [THEME_MODES.LIGHT]: 'Switch to dark theme',
  [THEME_MODES.DARK]: 'Switch to light theme',
};

function ThemeModeToggle({ variant = 'default', className }) {
  const classes = useStyles();
  const { settings, saveSettings } = useSettings();
  // Hidden while dark inventory theme is disabled; keep component for later.
  if (!INVENTORY_UI_FLAGS.enableDarkTheme) {
    return null;
  }

  const themeMode = settings.themeMode === THEME_MODES.LIGHT
    ? THEME_MODES.LIGHT
    : THEME_MODES.DARK;

  const handleClick = () => {
    saveSettings({ themeMode: nextThemeMode(themeMode) });
  };

  const Icon = themeMode === THEME_MODES.LIGHT ? Sun : Moon;
  const label = MODE_LABEL[themeMode];

  const buttonClass = [
    classes.root,
    variant === 'circle' ? classes.circle : '',
    variant === 'topBar' ? classes.topBar : '',
    className || '',
  ].filter(Boolean).join(' ');

  return (
    <Tooltip title={label}>
      <IconButton
        className={buttonClass}
        onClick={handleClick}
        aria-label={label}
        size="small"
      >
        <Icon size={variant === 'circle' ? 22 : 20} />
      </IconButton>
    </Tooltip>
  );
}

ThemeModeToggle.propTypes = {
  variant: PropTypes.oneOf(['default', 'circle', 'topBar']),
  className: PropTypes.string,
};

export default ThemeModeToggle;
